import { axiosInstance } from './axiosManager'

// The Code Coach substudy's chat (workspace/study/cc/CcChat.jsx) and its tutor on the server
// (backend ccTutor, /api/v1/cc). The server decides everything about a help round from its own
// records - the student's condition and arm, whether a "Your prediction" card is shown, when the
// Evidence Checker answers - so these calls carry only what the student did: their text, their
// choice on the card, and their code and latest run (ccContext.js). The page never learns the
// arm: an observe round simply comes back finished, and a participation prompt is only ever in a
// reply that shows the card.
//
// The calls go through axiosInstance, which carries the student's sign-in (the server checks who
// is asking, unlike /gpt/studentChat). Every call that starts or continues a round carries a
// clientRequestId (studentChatService.newClientRequestId): the server answers a resent id with
// what it stored for the first, so a resend after a lost answer never makes a second round.

const CC_URL = process.env.REACT_APP_HOST + '/api/v1/cc'

// A round can take two model calls (Code Coach, then Evidence Checker), each with one repair
// attempt, so it outlasts axiosInstance's 10 s by far. A wait that runs out is no harm: a
// question's Try again resends the same id and gets the stored reply, and the page looks up what
// became of a card's answer in the server's state (CcChat).
export const CC_ROUND_TIMEOUT_MS = 120 * 1000

const isText = (value) => typeof value === 'string'
const isId = (value) => (isText(value) && value !== '') || Number.isFinite(value)

export const CC_ROLES = ['student', 'coach', 'checker', 'contribution', 'notice']

// One message as the chat shows it: { id, roundId, role, text, at }, ids as strings. A message of
// an unknown shape is left out rather than shown half built.
export const normalizeCcMessage = (message) => {
  if (!message || typeof message !== 'object') return null
  if (!isId(message.id) || !CC_ROLES.includes(message.role) || !isText(message.text)) return null
  return {
    id: String(message.id),
    roundId: isId(message.roundId) ? String(message.roundId) : null,
    role: message.role,
    text: message.text,
    at: isText(message.at) || Number.isFinite(message.at) ? message.at : null,
  }
}

export const normalizeCcMessages = (messages) =>
  (Array.isArray(messages) ? messages : []).map(normalizeCcMessage).filter(Boolean)

// The card the server says is waiting: { roundId, prompt }, or null. Only a prompt the server
// sent is ever shown, so anything without one is no card at all.
export const normalizeCcPending = (pending) =>
  pending && isId(pending.roundId) && isText(pending.prompt) && pending.prompt.trim()
    ? { roundId: String(pending.roundId), prompt: pending.prompt }
    : null

// The round the server says it is still answering: { roundId, role: 'coach' | 'checker' }, null
// for none - or undefined when the server does not say (one without the field, or with a value
// of an unknown shape), and the chat then goes by the conversation's last message (CcChat).
export const normalizeCcGenerating = (generating) => {
  if (generating === null) return null
  if (generating && isId(generating.roundId) && (generating.role === 'coach' || generating.role === 'checker')) {
    return { roundId: String(generating.roundId), role: generating.role }
  }
  return undefined
}

// GET /cc/:sessionId/state as the chat uses it: the delivered messages in order, the waiting card
// (a reload brings it back), the round still being answered, whether the coding time is over, and
// the disclosure line (null: the page's own copy of it).
export const normalizeCcState = (data) => ({
  disclosure: isText(data?.disclosure) && data.disclosure.trim() ? data.disclosure : null,
  codingOver: data?.codingOver === true,
  pending: normalizeCcPending(data?.pending),
  generating: normalizeCcGenerating(data?.generating),
  messages: normalizeCcMessages(data?.messages),
})

// A round's reply (requests, contribution, checker): { round: { id, kind, status }, messages,
// pending, checkerFailed }. pending is the card, and only for a round the server says awaits the
// student's contribution, with the prompt it sent. checkerFailed: the Code Coach's message came
// but the Evidence Checker's did not, so Try again asks for the checker alone.
export const normalizeCcRoundReply = (data) => {
  const round = data?.round
  const roundId = isId(round?.id) ? String(round.id) : null
  const status = isText(round?.status) ? round.status : null
  const prompt = data?.participation?.prompt
  return {
    round: roundId ? { id: roundId, kind: isText(round.kind) ? round.kind : null, status } : null,
    messages: normalizeCcMessages(data?.messages),
    pending:
      roundId && status === 'awaiting_contribution' && isText(prompt) && prompt.trim() ? { roundId, prompt } : null,
    checkerFailed: data?.checkerFailed === true || status === 'checker_failed',
  }
}

// What a failed call says: { status, reason, message, pending }. status null: no answer came at
// all (offline, or the wait ran out), so the server may or may not have done it.
export const ccFailureOf = (error) => {
  const response = error?.response
  const data = response?.data && typeof response.data === 'object' ? response.data : {}
  return {
    status: Number.isInteger(response?.status) ? response.status : null,
    reason: isText(data.reason) ? data.reason : null,
    message: isText(data.message) && data.message.trim() ? data.message : null,
    pending: normalizeCcPending(data.pending),
  }
}

const sessionUrl = (sessionId) => `${CC_URL}/${encodeURIComponent(sessionId)}`
const roundUrl = (sessionId, roundId) => `${sessionUrl(sessionId)}/rounds/${encodeURIComponent(roundId)}`

export const getCcState = async (sessionId) => {
  const res = await axiosInstance.get(`${sessionUrl(sessionId)}/state`)
  return normalizeCcState(res.data)
}

// A new question: the Code Coach's message, and then either the card (a mediate round within its
// opportunities) or the Evidence Checker's message.
export const postCcRequest = async (sessionId, { clientRequestId, text, context }) => {
  const res = await axiosInstance.post(
    `${sessionUrl(sessionId)}/requests`,
    { clientRequestId, text, context },
    { timeout: CC_ROUND_TIMEOUT_MS }
  )
  return normalizeCcRoundReply(res.data)
}

// The card's answer: action 'submit' with the student's text, or 'skip' ("I'm not sure—continue")
// with none. The Evidence Checker answers in the same call.
export const postCcContribution = async (sessionId, roundId, { clientRequestId, action, text, context }) => {
  const body = action === 'submit' ? { clientRequestId, action, text, context } : { clientRequestId, action, context }
  const res = await axiosInstance.post(`${roundUrl(sessionId, roundId)}/contribution`, body, {
    timeout: CC_ROUND_TIMEOUT_MS,
  })
  return normalizeCcRoundReply(res.data)
}

// The Evidence Checker again, for a round whose checker failed.
export const postCcChecker = async (sessionId, roundId, { clientRequestId, context }) => {
  const res = await axiosInstance.post(
    `${roundUrl(sessionId, roundId)}/checker`,
    { clientRequestId, context },
    { timeout: CC_ROUND_TIMEOUT_MS }
  )
  return normalizeCcRoundReply(res.data)
}

// "Start a new question": the waiting card's round ends without a contribution.
export const postCcCancel = async (sessionId, roundId) => {
  await axiosInstance.post(`${roundUrl(sessionId, roundId)}/cancel`, {})
}

// Which messages and cards this page has shown: items [{ messageId, kind: 'coach' | 'checker' |
// 'prompt' }]. The server keeps the first time it hears of each.
export const postCcRendered = async (sessionId, items) => {
  await axiosInstance.post(`${sessionUrl(sessionId)}/rendered`, { items })
}
